import React from "react";
import { 
  FaShieldAlt, 
  FaBug, 
  FaNetworkWired, 
  FaCheck, 
  FaLock, 
  FaArrowRight, 
  FaServer, 
  FaHeadset, 
  FaDownload 
} from 'react-icons/fa';
import "./Antivirus.css";

const Antivirus = () => {
  const [scanMode, setScanMode] = React.useState('endpoint'); // 'endpoint', 'network', 'database'
  const [scanStatus, setScanStatus] = React.useState('scanning'); // 'scanning' or 'secured'

  React.useEffect(() => {
    // Cycle every 4.5 seconds between scanning modes
    const interval = setInterval(() => {
      setScanMode(prev => {
        if (prev === 'endpoint') return 'network';
        if (prev === 'network') return 'database';
        return 'endpoint';
      });
    }, 4500);

    return () => clearInterval(interval);
  }, []);

  // Separate effect to handle verification delay whenever the mode swaps
  React.useEffect(() => {
    setScanStatus('scanning');
    const timer = setTimeout(() => {
      setScanStatus('secured');
    }, 1800); // 1.8 seconds scanning animation

    return () => clearTimeout(timer);
  }, [scanMode]);

  const services = [
    { 
      name: "Antivirus Protection", 
      icon: <FaShieldAlt />,
      imgSrc: "/images/anti/1.jpg", 
      description: "Comprehensive real-time antivirus defense to shield all your business and personal endpoints from active exploits.",
      features: [
        "Real-Time File Scanning",
        "AI-Driven Threat Detection",
        "USB & External Drive Shield",
        "Auto-Updating Database"
      ]
    },
    { 
      name: "Malware Removal", 
      icon: <FaBug />,
      imgSrc: "/images/anti/10.png", 
      description: "Deep-system disinfection services to safely isolate and destroy hidden ransomware, spyware, and tracking software.",
      features: [
        "Advanced Spyware Purge",
        "Ransomware File Recovery",
        "Adware & Bloatware Cleanup",
        "Rootkit Destruction"
      ]
    },
    { 
      name: "Firewall & Network Security", 
      icon: <FaNetworkWired />,
      imgSrc: "/images/anti/9.jpg", 
      description: "Next-generation firewall management and configuration to monitor network traffic and filter digital intrusion attempts.",
      features: [
        "Intrusion Prevention (IPS)",
        "Smart Network Packet Filter",
        "Port Scan Attack Blocker",
        "Secured Local DNS Shieling"
      ]
    },
  ];

  const antivirusProducts = [
    { 
      name: "K7 Antivirus", 
      badge: "Top Value",
      imgSrc: "/images/k7-total-security-1-pc-1-year-500x500-min-removebg-preview.png", 
      description: "Ultra-fast scanning engine with negligible memory impact, providing lightning-fast scans.",
      license: "1 Device / 1 Year",
      features: ["Zero Performance Lag", "E-Commerce Secure Pay", "Parental Control Shields"]
    },
    { 
      name: "Norton Security", 
      badge: "Best Protection",
      imgSrc: "/images/anti/6__2_-removebg-preview.png", 
      description: "Advanced multi-layered security with cloud backup storage and integrated password generation tools.",
      license: "3 Devices / 1 Year",
      features: ["50GB Secure Cloud Backup", "Smart Traffic Firewall", "Dark Web Monitoring Scan"]
    },
    { 
      name: "McAfee Total Protection", 
      badge: "Family Champion",
      imgSrc: "/images/anti/267210_ip13ww-removebg-preview.png", 
      description: "All-in-one security suite with secure VPN, biometric manager, and identity monitoring dashboards.",
      license: "Multi-Device / 1 Year",
      features: ["Unlimited VPN Protection", "Identity Theft Protection", "Web Safety Advisor"]
    },
    { 
      name: "Bitdefender Total Security", 
      badge: "Editors' Choice",
      imgSrc: "/images/anti/b.png", 
      description: "Innovative AI-driven behavioral scanning providing peerless protection against fresh ransomware waves.",
      license: "1 Device / 1 Year",
      features: ["Behavioral Ransomware Shield", "Webcam & Mic Guard", "Automated Security Autopilot"]
    },
    { 
      name: "Avast Premium Security", 
      badge: "Essential Security",
      imgSrc: "/images/anti/a.png", 
      description: "Robust, flexible firewall security combining cloud sandbox file analysis with webcam intrusion alerts.",
      license: "1 PC / 1 Year",
      features: ["Sandbox File Verification", "Real-Time Wi-Fi Inspector", "Remote Access Blocker"]
    },
    { 
      name: "Malwarebytes Premium", 
      badge: "Ultimate Cleanup",
      imgSrc: "/images/anti/watchdog-anti-malware-2020-1y1u-3d-en-500x500-500x500.webp", 
      description: "Outstanding zero-day threat blocker engineered to stop malicious downloads and hijack attempts instantly.",
      license: "1 Device / 1 Year",
      features: ["Zero-Day Exploit Defense", "Ransomware Neutralizer", "Malicious Website Blocker"]
    },
  ];

  return (
    <div className="antivirus-root">
      {/* ── 1. CINEMATIC UNIFIED HERO BANNER ── */}
      <section 
        className="antivirus-hero" 
        style={{ backgroundImage: 'linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.75)), url("/antivirus/Antivirus-cover.png")' }}
      >
        <div className="antivirus-hero-inner">
          <div className="antivirus-hero-content">
            <span className="hero-tagline">
              <FaLock className="tagline-icon" /> Next-Generation Cybersecurity
            </span>
            <h1 className="hero-title">
              Enterprise-Grade <br />
              <span className="gradient-text">Endpoint Security</span>
            </h1>
            <p className="hero-desc">
              iMatrix Technologies secures your business infrastructure, computers, and digital networks. Deploy AI-powered antivirus suites, manage smart firewalls, and mitigate complex zero-day threats before they reach your workspace.
            </p>
            <div className="hero-ctas">
              <a href="#services" className="btn-primary">
                Explore Services <FaArrowRight className="btn-arrow" />
              </a>
              <a href="#products" className="btn-secondary">
                View Products
              </a>
            </div>
          </div>

          <div className="antivirus-hero-visual">
            <div className={`hero-scanner-card ${scanMode} ${scanStatus}`}>
              <div className="scanner-header">
                <span className="live-badge">
                  {scanMode === 'endpoint' && 'ENDPOINT SHIELD'}
                  {scanMode === 'network' && 'FIREWALL GATEWAY'}
                  {scanMode === 'database' && 'THREAT DATABASE'}
                </span>
                <span className="scanner-status">
                  {scanStatus === 'scanning' ? 'SCANNING ACTIVE...' : 'SYSTEM SECURED'}
                </span>
              </div>

              <div className="scanner-target-wrapper">
                <div className="scanner-target-corner top-left"></div>
                <div className="scanner-target-corner top-right"></div>
                <div className="scanner-target-corner bottom-left"></div>
                <div className="scanner-target-corner bottom-right"></div>
                <div className="scanner-sweep-line"></div>
                
                <div className="avatar-circle">
                  {scanMode === 'endpoint' && <FaShieldAlt className="scanner-icon shield-icon" />}
                  {scanMode === 'network' && <FaNetworkWired className="scanner-icon network-icon" />}
                  {scanMode === 'database' && <FaServer className="scanner-icon server-icon" />}
                </div>
              </div>

              <div className="scanner-result">
                <div className="pulse-success-indicator"></div>
                <span className="result-text">
                  {scanStatus === 'scanning' ? (
                    scanMode === 'endpoint' ? 'SHIELD: SCANNING FILES...' :
                    scanMode === 'network' ? 'FIREWALL: FILTERING PORTS...' :
                    'DATABASE: INDEXING DEFINITIONS...'
                  ) : (
                    scanMode === 'endpoint' ? 'SHIELD: ENDPOINTS SECURED (100%)' :
                    scanMode === 'network' ? 'FIREWALL: MALICIOUS TRAFFIC BLOCKED' :
                    'DATABASE: 100% SYNCHRONIZED'
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── 2. SERVICES SECTION (3 columns desktop, full width) ── */}
      <section id="services" className="antivirus-services-section">
        <div className="container-wide">
          <div className="section-header-centered">
            <span className="sub-title">WHAT WE DO</span>
            <h2 className="section-title">Professional Threat Mitigation</h2>
            <p className="section-desc">
              Our specialized engineers configure, maintain, and execute comprehensive security policies to keep your databases, laptops, and endpoints clean.
            </p>
          </div>

          <div className="services-grid-modern">
            {services.map((service, index) => (
              <div key={index} className="service-card-modern">
                <div className="card-top-image">
                  <img src={service.imgSrc} alt={service.name} />
                  <div className="card-floating-icon">{service.icon}</div>
                </div>
                
                <div className="card-body-modern">
                  <h3 className="service-card-title">{service.name}</h3>
                  <p className="service-card-desc">{service.description}</p>
                  
                  <div className="features-divider"></div>
                  
                  <ul className="service-features-list">
                    {service.features.map((feat, i) => (
                      <li key={i}>
                        <FaCheck className="check-icon-mini" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. ASSURANCES BANNER ── */}
      <section className="security-assurances-banner">
        <div className="container-wide assurance-grid">
          <div className="assurance-item">
            <div className="assurance-icon-box">
              <FaServer />
            </div>
            <div className="assurance-text">
              <h4>Express Endpoint Setup</h4>
              <p>Remote and on-site expert license activation and setup support.</p>
            </div>
          </div>
          
          <div className="assurance-item">
            <div className="assurance-icon-box">
              <FaHeadset />
            </div>
            <div className="assurance-text">
              <h4>24/7 Security Assistance</h4>
              <p>Continuous threat troubleshooting and software updates by technicians.</p>
            </div>
          </div>

          <div className="assurance-item">
            <div className="assurance-icon-box">
              <FaDownload />
            </div>
            <div className="assurance-text">
              <h4>Genuine Softwares</h4>
              <p>Authorized partner retail channels ensuring clean database keys.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. PRODUCTS SHOWROOM SECTION (Desktop full width 3-4 columns) ── */}
      <section id="products" className="antivirus-showroom-section">
        <div className="container-wide">
          <div className="section-header-centered">
            <span className="sub-title">RECOMMENDED SOFTWARE</span>
            <h2 className="section-title">Verified Shield Providers</h2>
            <p className="section-desc">
              Browse elite personal and business cybersecurity suites. We supply authentic license keys and facilitate smooth setup on all endpoints.
            </p>
          </div>

          <div className="products-grid-modern">
            {antivirusProducts.map((product, index) => (
              <div key={index} className="product-card-modern">
                <div className="product-badge-wrap">
                  <span className="product-badge">{product.badge}</span>
                </div>
                
                <div className="product-image-container">
                  <img src={product.imgSrc} alt={product.name} />
                </div>

                <div className="product-info-modern">
                  <h3 className="product-name">{product.name}</h3>
                  <span className="license-info">{product.license}</span>
                  <p className="product-desc">{product.description}</p>
                  
                  <div className="features-divider"></div>
                  
                  <ul className="product-feature-ticks">
                    {product.features.map((feat, i) => (
                      <li key={i}>
                        <FaCheck className="tick-icon" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <a 
                    href="/contact" 
                    className="product-cta-btn"
                  >
                    Request Quote <FaArrowRight className="cta-arrow-icon" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Antivirus;
